/* eslint-disable @typescript-eslint/no-explicit-any */
import { Theme } from '@emotion/react';
import { SxProps } from '@mui/material';
import {
  DataGridPremium,
  GridActionsColDef,
  GridCellEditStopParams,
  GridColDef,
  GridFilterItem,
  GridFilterModel,
  GridLogicOperator,
  GridRowParams,
  GridSortModel,
  useGridApiRef
} from '@mui/x-data-grid-premium';
import { GridInitialStatePremium } from '@mui/x-data-grid-premium/models/gridStatePremium';
import { useAppSelector } from 'store/store';
import {
  getPaginationDefault,
  setPaginationDefault
} from 'components/enhanced/data-grid/utils/paginationHelper';
import { ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN } from 'shared/permissions/roles';
import toast from 'shared/utils/toast';
import { MutableRefObject, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BaseService } from 'shared/services/BaseService';
import { v4 as uuidv4 } from 'uuid';
import { RowAction } from '../types/enhanced.types';
import { ToolbarAction, ToolbarFilter } from '../types/toolbar.types';
import { CustomEmptyOverlay } from './components/CustomEmptyOverlay';
import { RowContextMenu } from './components/RowContextMenu';
import { DataGridToolbar } from './components/toolbar/DataGridToolbarAdapter';
import { useGridUpdate } from './hooks/useGridUpdate';

/** Column fields that represent warehouse filtering in API query params. */
function isWarehouseScopedColumnField(field: string | undefined): boolean {
  if (!field) return false;
  if (field === 'warehouses') return true;
  return field.includes('warehouse');
}

const PAGE_SIZE_OPTIONS = [5, 10, 20, 50, 100, 500, 1000, 2000, 5000];

/**
 * Property contract contract managing behaviors and configurations for the EnhancedDataGrid.
 */
interface EnhancedDataGridProps {
  /** Core API communication framework orchestration module powering data operations */
  service: BaseService<any>;
  columns: GridColDef[];
  getMethod?: (pageSize: number, offset: number, params: Record<string, string>) => Promise<any>;
  /** Immutably bound base dictionary query criteria parameters injected automatically */
  initialParams?: Record<string, string>;
  /**
   * Explicit API query parameter name for warehouse filtering (e.g. "warehouse" or
   * "warehouse_code"). When omitted, the grid tries to auto-detect the warehouse column.
   */
  warehouseFilterParam?: string;
  /** External macro target anchor enabling remote table query re-invocations */
  refresh?: MutableRefObject<any>;
  /** Context bridge object extracting or forcing active row listings across components */
  rows?: MutableRefObject<any> | any;
  onDataLoaded?: (items: any[], meta: { count: number }) => void;
  /** Dynamic context filters supplied on execution by cross-component layout nodes */
  outerParams?: any;
  /** Global query key targeting full-text string properties across models */
  fullTextQuery?: string;
  /** Toggle switch bypassing network execution layers to perform browser-only calculations */
  clientSide?: boolean;
  /** Pre-configured complex advanced criteria block array setups loaded on initialization */
  initialFilterItems?: GridFilterItem[];
  /** Baseline grid structure definitions parsed down to restore layout frameworks */
  initialOptions?: Partial<GridInitialStatePremium>;
  /** Toggle switch forcing typography rows to adopt compact spacing metrics */
  dense?: boolean;
  /** Interactive operational toggle controlling checklist checkboxes on the extreme left */
  checkboxSelection?: boolean;
  /** Enforcement parameter revoking aggregate selection actions across matrix items */
  disableMultipleSelection?: boolean;
  /** Mutation event callback capturing data changes during row selection adjustments */
  selectionModelChange?: any;
  /** Dynamic layout expansion payload injecting details grid components below rows */
  getDetailPanelContent?: any;
  /** Authorization switch revoking native platform row purging capabilities */
  disableDelete?: boolean;
  /** Authorization switch revoking cell input mutations on active views */
  disableEdit?: boolean;
  /** Toggle switch overriding and revoking data layout migration options */
  disableExport?: boolean;
  /** Encapsulated theme parameters fine-tuning specific layout targets */
  /** Preset design configuration tokens mapping standard application frames */
  variant?: 'standard' | 'friendly' | 'dense-custom';
  /** Theme-driven structural style engine overrides customizing the entire data grid canvas */
  sx?: SxProps<Theme>;
  /** Configuration switch indicating whether to render data-driven shared toolbar wrappers */
  useGenericToolbar?: boolean;
  /** Declarative design configuration object powering generic unified toolbars */
  genericToolbarConfig?: {
    /** Array containing declarative widget config setups passed downstream to input widgets */
    filters?: ToolbarFilter[];
    /** Actions layout schema generating operation nodes inside toolbar sections */
    actions?: ToolbarAction[];
    /** Style overrides targeting specific custom wrapper canvases */
    sx?: SxProps<Theme>;
  };
  /** Structural visibility flag mapping native platform layout tools */
  showGridControls?: boolean;
  /** Action definitions building custom context dropdown lists attached to individual rows */
  rowActions?: RowAction[];
  /** Toggle switch removing text descriptive layout cell matrices from viewports */
  hideHeader?: boolean;
  onRowClick?: (params: GridRowParams) => void;
  onCellEditCommitOverride?: (params: GridCellEditStopParams, event: any, apiRef: any) => void;
}

/**
 * Fully featured DataGrid presentation framework wrapper built upon the MUI Premium Grid engine.
 * Automates asynchronous lifecycle syncs, local state preservation pipelines, cross-field warehouse mappings,
 * row id mapping fallback overrides, and unified data-driven toolbar abstraction integrations.
 *
 * @param props - Custom interface constraints defined by EnhancedDataGridProps.
 * @returns Comprehensive synchronized premium analytical data grid grid layout.
 */
function EnhancedDataGrid({
  service,
  columns,
  getMethod,
  initialParams,
  warehouseFilterParam,
  refresh,
  rows,
  onDataLoaded,
  outerParams,
  fullTextQuery,
  clientSide,
  initialFilterItems,
  initialOptions,
  dense,
  checkboxSelection,
  disableMultipleSelection,
  selectionModelChange,
  getDetailPanelContent,
  disableDelete,
  disableEdit,
  disableExport,
  sx,
  useGenericToolbar = false,
  genericToolbarConfig,
  showGridControls = true,
  rowActions,
  hideHeader,
  onRowClick,
  onCellEditCommitOverride
}: EnhancedDataGridProps) {
  const { t } = useTranslation();

  const groups = useAppSelector((state) => state.auth.groups);
  const startingPageSize = getPaginationDefault();
  const apiRef = useGridApiRef();

  const [skip, setSkip] = useState(false);
  const [pageSize, setPageSize] = useState(startingPageSize || 10);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const [items, setItems] = useState<any[]>([]);
  const [orderMacro, setOrderMacro] = useState('');
  const [filter, setFilter] = useState<Record<string, string>>(null);

  const defaultWarehouse = useAppSelector((state) => state.auth.warehouse);

  const hasActiveWarehouse = Boolean(
    defaultWarehouse != null && String(defaultWarehouse).trim().length > 0
  );

  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: []
  });

  const [sortModel, setSortModel] = useState<GridSortModel>([]);

  const [preferenceKey, setPreferenceKey] = useState('');

  const [firstRender, setFirstRender] = useState(true);

  // 1a. Serializza outerParams per usarlo in modo sicuro nelle dipendenze
  const outerParamsString = useMemo(() => {
    return outerParams ? JSON.stringify(outerParams) : '';
  }, [outerParams]);

  // 1b. Crea un riferimento stabile per gli slot props (evita `{}` inline)
  const stableOuterParams = useMemo(() => {
    return outerParams || {};
  }, [outerParamsString]); // Ricalcola solo se i valori interni cambiano davvero

  // Lifecycle Sync Phase 1: Re-triggers query updates whenever dynamic filter properties alter state
  useEffect(() => {
    if (firstRender) {
      // if it's the first render, change the state to trigger the useState
      setFirstRender(false);
    } else {
      refreshColumnsData();
    }
  }, [
    pageSize,
    page,
    fullTextQuery,
    orderMacro,
    filter,
    outerParamsString,
    defaultWarehouse,
    hasActiveWarehouse,
    filterModel.items,
    sortModel
  ]);

  // FILTRI PERSISTENTI - ATTENZIONE !!!
  // Lifecycle Sync Phase 2: Restores historical session layout configurations from cached LocalStorage bounds
  useEffect(() => {
    if (firstRender === false) {
      // first render -> restore the state and trigger refresh
      const pk = btoa(
        JSON.stringify({
          service: service.modulePrefix,
          getMethod,
          columns: columns.map((col) => col.field)
        })
      );
      setPreferenceKey(pk);
      const datagridState = localStorage.getItem(pk);
      if (datagridState) {
        apiRef.current.restoreState(JSON.parse(datagridState));
      } else {
        refreshColumnsData();
      }
    }
  }, [firstRender]);

  /**
   * Captures and serializes dynamic UI layout properties down into local device session storage.
   */
  const exportState = useCallback(() => {
    if (apiRef && preferenceKey) {
      const currentState = apiRef.current.exportState();
      localStorage.setItem(
        preferenceKey,
        JSON.stringify({
          columns: currentState.columns,
          filter: currentState.filter,
          sorting: currentState.sorting
        })
      );
    }
  }, [apiRef, preferenceKey]);

  /**
   * Automatically persists structural preference changes on filter matrix changes
   */
  useEffect(() => {
    exportState();
  }, [filterModel.items, sortModel, exportState]);

  /**
   * Primary asynchronous fetch utility coordinating backend parameters and executing query requests.
   */
  const refreshColumnsData = () => {
    if (!isLoading) {
      setIsLoading(true);
      let searchParams = {};
      filterModel.items.map((element) => {
        searchParams = { ...searchParams, ..._getFilterObj(element) };
      });

      if (!skip && initialFilterItems) {
        let filters = {};
        initialFilterItems.forEach((element) => {
          filters = { ...filters, ..._getFilterObj(element) };
        });
        searchParams = { ...searchParams, ...filters };
      }

      // Se l'utente ha un magazzino attivo (assegnato o selezionato in alto), forza il parametro
      // API corrispondente, a meno che l'utente non abbia già filtrato esplicitamente una colonna
      // magazzino: in quel caso il filtro scelto ha la precedenza (e resta comunque limitato
      // dallo scoping del backend).
      if (defaultWarehouse && hasActiveWarehouse) {
        const warehouseKey =
          warehouseFilterParam ||
          columns.find((col) => isWarehouseScopedColumnField(col.field))?.field;
        if (warehouseKey && searchParams[warehouseKey] === undefined) {
          searchParams = { ...searchParams, [warehouseKey]: defaultWarehouse };
        }
      }

      if (fullTextQuery) {
        searchParams['q'] = fullTextQuery;
      }
      if (orderMacro.trim().length !== 0) {
        searchParams['ordering'] = orderMacro;
      }
      if (filter) {
        searchParams = { ...searchParams, ...filter };
      }
      if (stableOuterParams && Object.keys(stableOuterParams).length > 0) {
        searchParams = { ...searchParams, ...stableOuterParams };
      }
      // Sempre a ogni richiesta: prima skip=true initialParams non venivano più inviati → dati aggregati stantii.
      if (initialParams) {
        searchParams = { ...searchParams, ...initialParams };
      }
      if (!skip && initialParams) {
        setSkip(true);
      }
      if (getMethod) {
        getMethod(pageSize, page * pageSize, searchParams)
          .then((res) => {
            const results = res.results;
            const mapped = results.map((item) => {
              // stock_distinct_per_location: più righe per stesso id → usare row_id come chiave
              if (item.row_id != null) return { ...item, id: item.row_id };
              // aggregated-contract / righe senza id: chiave stabile per aggiornare colonne dopo refresh
              if (!item?.id && item.contract_equipment_id != null) {
                return { ...item, id: item.contract_equipment_id };
              }
              if (!item?.id) return { ...item, id: uuidv4() };
              return item;
            });
            setItems(mapped);
            setCount(res.count);
            if (onDataLoaded) onDataLoaded(mapped, { count: res.count });
            setIsLoading(false);
          })
          .catch(() => {
            searchParams = {};
            setIsLoading(false);
            setFilter(null);
            setPage(0);
          });
      } else {
        service
          .getAllPaginated(pageSize, page * pageSize, searchParams)
          .then((res) => {
            setItems(res.results);
            setCount(res.count);
            if (onDataLoaded) onDataLoaded(res.results, { count: res.count });
            setIsLoading(false);
          })
          .catch(() => {
            searchParams = {};
            setIsLoading(false);
            // setFilter(null);
            setPage(0);
          });
      }
    }
  };

  // Lifecycle Sync: Registers reactive observer events tracking user changes on the grid layout columns
  useEffect(() => {
    if (!apiRef.current) return;

    const unsubscribeOrder = apiRef.current.subscribeEvent('columnOrderChange', exportState);

    const unsubscribeWidth = apiRef.current.subscribeEvent('columnResize', exportState);

    const unsubscribeVisibility = apiRef.current.subscribeEvent(
      'columnVisibilityModelChange',
      exportState
    );

    return () => {
      unsubscribeOrder();
      unsubscribeWidth();
      unsubscribeVisibility();
    };
  }, [apiRef, exportState]);

  // Lifecycle Sync: Binds parent refresh macro hooks and initializes standard base layout pagination sizes
  useEffect(() => {
    const startingPageSize = getPaginationDefault();
    if (startingPageSize) setPageSize(startingPageSize);
  }, []);

  // Mantieni sempre l’ultima refreshColumnsData (evita ref “congelata” al primo render → colonne obsolete).
  useEffect(() => {
    if (refresh) refresh.current = refreshColumnsData;
  }, [refresh, refreshColumnsData]);

  // Lifecycle Sync: Mirror-syncs working collection nodes directly across the shared reference bridge
  useEffect(() => {
    if (rows) rows.current = items;
  }, [items]);

  // Lifecycle Sync: Caches newly adjusted pagination size preferences directly down to device memory
  useEffect(() => {
    if (pageSize) {
      setPaginationDefault(pageSize);
    }
  }, [pageSize]);

  // Lifecycle Sync: Drops current pagination markers down to index 0 whenever sorting or query criteria alters data
  useEffect(() => {
    setPage(0);
  }, [fullTextQuery, filter, orderMacro]);

  /**
   * Capture and maps native DataGrid premium sort actions downstream into valid Django string tokens.
   */
  const onSortingChange = (x) => {
    setSortModel(x);
    if (x.length > 0) setOrderMacro(`${x[0].sort === 'desc' ? '-' : ''}${x[0].field}`);
    else setOrderMacro('');
  };

  /**
   * Captures, aggregates, and transforms active filter matrices into a flat query map for Django.
   */
  const onFilterChange = (x) => {
    setFilterModel(x);
    let filters = initialParams ? { ...initialParams } : {};

    // Usa forEach invece di map (visto che non ritorni un array)
    x.items.forEach((element) => {
      filters = { ...filters, ..._getFilterObj(element) };
    });
    setFilter(filters);
  };

  /**
   * Invokes delete mutations across target rows and refreshes the primary dataset canvas.
   */
  const handleOnDelete = (id: number) => {
    service.delete(id).then(() => {
      refreshColumnsData();
      toast.success(t('actions.successfully.deleted'));
    });
  };

  function _getFilterObj(item): Record<string, string> {
    if (!item?.field) {
      return {};
    }
    const value = item?.value;
    const hasValue =
      value !== null &&
      value !== undefined &&
      (typeof value !== 'string' || value.trim().length > 0);

    if (item.operator === 'is' && typeof value === 'boolean') {
      return { [item.field]: String(value) };
    }
    if (['contains', '=', 'is'].includes(item.operator) && hasValue) {
      return { [item.field]: String(value) };
    } else if (['equals'].includes(item.operator) && hasValue) {
      return { [`${item.field}`]: String(value), [`${item.field}__exact`]: 'true' };
    } else if (['startsWith'].includes(item.operator) && hasValue) {
      return {
        [`${item.field}`]: String(value),
        [`${item.field}__startswith`]: 'true'
      };
    } else if (['endsWith'].includes(item.operator) && hasValue) {
      return {
        [`${item.field}`]: String(value),
        [`${item.field}__endswith`]: 'true'
      };
    } else if (['>', 'after'].includes(item.operator) && hasValue) {
      return { [`${item.field}__gt`]: String(value) };
    } else if (['>=', 'onOrAfter'].includes(item.operator) && hasValue) {
      return { [`${item.field}__gte`]: String(value) };
    } else if (['<', 'before'].includes(item.operator) && hasValue) {
      return { [`${item.field}__lt`]: String(value) };
    } else if (['<=', 'onOrBefore'].includes(item.operator) && hasValue) {
      return { [`${item.field}__lte`]: String(value) };
    } else if (['isEmpty'].includes(item.operator)) {
      return { [`${item.field}__isnull`]: 'true' };
    } else if (['isNotEmpty'].includes(item.operator)) {
      return { [`${item.field}__isnull`]: 'false' };
    } else {
      return null;
    }
  }

  /**
   * Handles server-side row persistence modifications whenever inline cellular data changes.
   */
  const { handleProcessRowUpdate } = useGridUpdate({
    service,
    refreshColumnsData,
    apiRef,
    disableEdit
  });

  const shouldRenderDefaultDelete = useMemo(() => {
    return (
      !!service?.canDelete &&
      !disableDelete &&
      !!groups &&
      groups.some((group) => [ROLE_SUPER_ADMIN, ROLE_TENANT_ADMIN].includes(group))
    );
  }, [service?.canDelete, disableDelete, groups]);

  const externalActionsColumn = useMemo(() => {
    return columns.find((col) => col.type === 'actions' || 'getActions' in col);
  }, [columns]);

  const shouldRenderUnifiedMenu = useMemo(() => {
    const hasGlobalRowActions = !!rowActions && rowActions.length > 0;
    const hasExternalActions = !!externalActionsColumn;
    return shouldRenderDefaultDelete || hasGlobalRowActions || hasExternalActions;
  }, [shouldRenderDefaultDelete, rowActions, externalActionsColumn]);

  const _columns = useMemo(() => {
    const cleanColumns = columns.filter((col) => col.type !== 'actions' && !('getActions' in col));

    const mappedColumns = cleanColumns.map((col) => {
      const lockWarehouseFilter = hasActiveWarehouse && isWarehouseScopedColumnField(col.field);
      return {
        ...col,
        editable: disableEdit ? false : !!col.editable,
        ...(lockWarehouseFilter ? { filterable: false } : {})
      };
    });

    if (shouldRenderUnifiedMenu) {
      mappedColumns.push({
        field: 'row_actions_unified_menu_key',
        type: 'actions',
        headerName: '',
        width: 50,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        editable: false,
        renderCell: (params) => {
          const actionsCol = externalActionsColumn as GridActionsColDef | undefined;

          const extractedPageActions = actionsCol?.getActions
            ? actionsCol.getActions(params as unknown as GridRowParams)
            : [];

          // Backward compatibility: support action columns implemented with renderCell.
          const legacyRenderedAction =
            !actionsCol?.getActions && actionsCol?.renderCell
              ? actionsCol.renderCell(params as any)
              : null;

          const totalCustomActions = [
            ...(rowActions || []),
            ...extractedPageActions,
            ...(legacyRenderedAction ? [legacyRenderedAction] : [])
          ];

          return (
            <RowContextMenu
              row={params.row}
              customActions={totalCustomActions}
              shouldRenderDelete={shouldRenderDefaultDelete}
              handleOnDelete={handleOnDelete}
              t={t}
            />
          );
        }
      });
    }

    return mappedColumns;
  }, [
    columns,
    disableEdit,
    hasActiveWarehouse,
    rowActions,
    externalActionsColumn,
    shouldRenderDefaultDelete,
    shouldRenderUnifiedMenu,
    handleOnDelete,
    t
  ]);

  const warehouseColumnField = useMemo(() => {
    if (!defaultWarehouse) return null;
    if (warehouseFilterParam) return warehouseFilterParam;
    const warehouseCol = columns.find((col) => isWarehouseScopedColumnField(col.field));
    return warehouseCol ? warehouseCol.field : null;
  }, [columns, defaultWarehouse, warehouseFilterParam]);

  const computedInitialState = useMemo(() => {
    const baseFilterItems = [...(initialFilterItems || [])];

    if (warehouseColumnField && !hasActiveWarehouse) {
      baseFilterItems.push({
        field: warehouseColumnField,
        operator: 'contains',
        value: defaultWarehouse
      });
    }

    return {
      filter: {
        filterModel: {
          items: baseFilterItems,
          logicOperator: GridLogicOperator.And,
          quickFilterLogicOperator: GridLogicOperator.And
        }
      },
      pinnedColumns: {
        left: ['__check__', 'name', 'code', 'approved', 'moved_quantity', 'quantity', 'id'],
        right: shouldRenderUnifiedMenu ? ['row_actions_unified_menu_key'] : []
      },
      ...(initialOptions || {})
    };
  }, [
    initialFilterItems,
    warehouseColumnField,
    hasActiveWarehouse,
    defaultWarehouse,
    shouldRenderUnifiedMenu,
    initialOptions
  ]);

  return (
    <DataGridPremium
      sx={sx}
      density={dense ? 'compact' : 'standard'}
      columnHeaderHeight={hideHeader ? 0 : 56}
      apiRef={apiRef}
      initialState={computedInitialState}
      disableRowSelectionOnClick
      columns={_columns}
      autosizeOnMount
      autosizeOptions={{
        includeHeaders: true, // Include la lunghezza del testo dell'header nel calcolo
        includeOutliers: false // Impostato a false per massimizzare le performance di rendering su grandi dataset
      }}
      loading={isLoading}
      rows={items}
      pagination
      paginationModel={{ page: page, pageSize: pageSize }}
      onPaginationModelChange={(model) => {
        setPage(model.page);
        setPageSize(model.pageSize);
      }}
      pageSizeOptions={PAGE_SIZE_OPTIONS}
      rowCount={count}
      paginationMode={clientSide ? 'client' : 'server'}
      sortingMode={clientSide ? 'client' : 'server'}
      filterMode={clientSide ? 'client' : 'server'}
      onSortModelChange={onSortingChange}
      onFilterModelChange={onFilterChange}
      sortModel={sortModel}
      filterModel={filterModel}
      processRowUpdate={handleProcessRowUpdate}
      onCellEditStop={onCellEditCommitOverride || undefined}
      checkboxSelection={checkboxSelection}
      disableMultipleRowSelection={disableMultipleSelection}
      onRowSelectionModelChange={selectionModelChange}
      getDetailPanelHeight={() => 'auto'}
      getDetailPanelContent={getDetailPanelContent ?? null}
      data-is-loading={isLoading}
      data-first-render={firstRender}
      onRowClick={onRowClick}
      slots={{
        noRowsOverlay: CustomEmptyOverlay,
        noResultsOverlay: CustomEmptyOverlay,
        toolbar: DataGridToolbar
      }}
      slotProps={{
        toolbar: {
          service,
          outerParams: stableOuterParams,
          useGenericToolbar,
          genericToolbarConfig,
          disableExport,
          showGridControls,
          disableEdit
        }
      }}
    />
  );
}

export default EnhancedDataGrid;
